import React, { useId } from 'react';
import { ACLOutDTO, VisibilityEnum } from '@api/ringil-api.v2';
import useTranslation from '@hooks/i18n/useTranslation.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import SvgLockClosed from '@icons/lock-closed.svg?react';
import SvgLockOpen from '@icons/lock-open.svg?react';

interface ComponentProps {
  acl: ACLOutDTO;
  dataTestId?: string;
}

/**
 * The lock goes inside a single child rather than into COBadge's own `icon` slot: that slot renders
 * after the text and with the badge's 16px gap, while these read as a prefix glyph. Both source SVGs
 * hardcode a black fill, so `fill-current` is what lets them take the badge's own colour.
 */
const withLock = (icon: React.ReactNode, label: string) => (
  <span className={'flex flex-row items-center gap-1'}>
    {icon}
    {label}
  </span>
);

const lockClass = 'w-4 h-4 [&_path]:fill-current';

const VisibilityBadge: React.FC<ComponentProps> = ({ acl, dataTestId }) => {
  const { tr } = useTranslation();
  // useId's value contains colons, which are not valid in a querySelector id — CustomTooltip looks its
  // target up by selector, so strip them and keep a letter-first prefix.
  const tooltipId = `visibility-badge-${useId().replace(/:/g, '')}`;

  if (acl?.visibility == null) return null;

  if (acl.visibility === VisibilityEnum.VISIBLE_TO_COMPANIES) {
    const companyNames = (acl.visibleToCompanies ?? []).map(company => company.name).join(', ');
    return (
      <span id={tooltipId} className={'inline-flex'}>
        <COBadge variant={'outlined'} type={'info'} className={'whitespace-nowrap'} dataTestId={dataTestId}>
          {withLock(<SvgLockOpen className={lockClass} />, tr('VisibilityBadge.visibilityToSelected', 'Visibility to selected'))}
        </COBadge>
        <CustomTooltip
          targetSelector={`#${tooltipId}`}
          content={
            <>
              <div>{tr('VisibilityBadge.visibilityToSelected2', 'Visibility to selected:')}</div>
              <div>{companyNames}</div>
            </>
          }
        />
      </span>
    );
  }

  if (acl.visibility === VisibilityEnum.PRIVATE) {
    return (
      <COBadge variant={'outlined'} type={'draft'} className={'whitespace-nowrap'} dataTestId={dataTestId}>
        {withLock(<SvgLockClosed className={lockClass} />, tr('VisibilityBadge.private', 'Private'))}
      </COBadge>
    );
  }

  return (
    <COBadge variant={'outlined'} type={'neutral'} className={'whitespace-nowrap'} dataTestId={dataTestId}>
      {tr('VisibilityBadge.visibleToAll', 'Visible to all')}
    </COBadge>
  );
};

export default VisibilityBadge;
